import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { ColumnDef } from "@tanstack/react-table";
import DataTable from "@/components/controls/DataTable/DataTable";
import UserAvatar from "@/components/controls/UserAvatar";
import IconContainer from "@/components/ui/icon-container";
import Text from "@/components/ui/text";
import {
  ExpandView,
  FileAlert,
  FileSigned,
  Regenerate
} from "@dreamhub/designsystem/icons";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import OptionsDropdownMenu, {
  DetailedViewOptions
} from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import { formatFullDate, toCalendarDate } from "@/utils/datetime";
import { useElementWidth } from "@/hooks/useElementWidth";
import { TrackedProposalStatus } from "./TrackedProposalStatus";
import {
  isDeletable,
  isDraftOnlyActionAllowed,
  isRegenerable,
  isRevisable,
  isWithdrawable
} from "./draftActions";
import { reviseOption, withdrawOption } from "./editor/menuOptions";
import { Proposal, ProposalRow } from "./types";

// An earlier version shares its proposal's id, so it is keyed by its own.
const TABLE_OPTIONS = {
  getRowId: (row: ProposalRow) => row.earlierVersionId ?? row.id
};

export interface ProposalRowActions {
  onOpen: (proposal: Proposal) => void;
  // Opens one earlier version of the proposal, to be read as it went out.
  onOpenVersion: (proposal: Proposal, versionId: string) => void;
  onRegenerate: (proposal: Proposal) => void;
  onDelete: (proposal: Proposal) => void;
  onRevise: (proposal: Proposal) => void;
  onWithdraw: (proposal: Proposal) => void;
}

interface Props extends ProposalRowActions {
  // Each proposal, followed by its earlier versions that went out.
  proposals: ProposalRow[];
  // Rows whose actions are held while one of them runs.
  busyIds?: ReadonlySet<string>;
  // Why Regenerate is unavailable; shown on the disabled option.
  regenerateBlockedReason?: string;
  // Holds Regenerate while the contract's Terms & Services are still loading.
  regenerateHeld?: boolean;
  // The e-sign connection is down, so an open proposal's status is its last
  // known one.
  trackingPaused?: boolean;
}

const NO_BUSY_IDS: ReadonlySet<string> = new Set();

// Every column but the form's; the form takes what's left of the row.
const FIXED_COLUMNS_WIDTH = 44 + 150 + 200 + 200 + 200 + 64;
const FORM_MIN_WIDTH = 160;
const FORM_DEFAULT_WIDTH = 338;

const formWidth = (tableWidth: number) =>
  tableWidth > 0
    ? Math.max(FORM_MIN_WIDTH, Math.floor(tableWidth - FIXED_COLUMNS_WIDTH))
    : FORM_DEFAULT_WIDTH;

const openOption = (action: () => void) => ({
  action,
  customIcon: (
    <IconContainer size="5" color="secondary">
      <ExpandView />
    </IconContainer>
  )
});

const openRow = (
  row: ProposalRow,
  {
    onOpen,
    onOpenVersion
  }: Pick<ProposalRowActions, "onOpen" | "onOpenVersion">
) =>
  row.earlierVersionId ? onOpenVersion(row, row.earlierVersionId) : onOpen(row);

// Matches the proposal's own menu: Delete only where contractsapi allows it,
// and Regenerate only on one still in play. Only a draft is edited; any other
// proposal opens to be read. An earlier version can only be opened.
const rowOptions = (
  proposal: ProposalRow,
  actions: ProposalRowActions,
  regenerateBlockedReason: string | undefined,
  regenerateHeld: boolean
): DetailedViewOptions => {
  const { onOpen, onRegenerate, onDelete, onRevise, onWithdraw } = actions;
  if (proposal.earlierVersionId) {
    return { open: openOption(() => openRow(proposal, actions)) };
  }
  return {
    ...(isDraftOnlyActionAllowed(proposal)
      ? { edit: () => onOpen(proposal) }
      : { open: openOption(() => onOpen(proposal)) }),
    // As on the proposal itself: offered wherever contractsapi allows it.
    ...(isRevisable(proposal)
      ? { revise: reviseOption(() => onRevise(proposal), false) }
      : {}),
    ...(isRegenerable(proposal)
      ? {
          regenerate: {
            action: () => onRegenerate(proposal),
            disabled: regenerateHeld || !!regenerateBlockedReason,
            disabledTooltipText: regenerateBlockedReason,
            customIcon: (
              <IconContainer size="5" color="secondary">
                <Regenerate />
              </IconContainer>
            )
          }
        }
      : {}),
    // Also on a closed deal, where there's no Revise.
    ...(isWithdrawable(proposal)
      ? { withdraw: withdrawOption(() => onWithdraw(proposal), false) }
      : {}),
    ...(isDeletable(proposal)
      ? {
          delete: {
            action: () => onDelete(proposal),
            variant: "destructive" as const
          }
        }
      : {})
  };
};

const buildProposalColumns = (
  t: TFunction,
  actions: ProposalRowActions,
  busyIds: ReadonlySet<string>,
  regenerateBlockedReason: string | undefined,
  regenerateHeld: boolean,
  nameWidth: number,
  trackingPaused: boolean
): ColumnDef<ProposalRow>[] => [
  {
    id: "icon",
    header: "",
    enableSorting: false,
    size: 44,
    // DataTable's default floor is 100px, wider than the column.
    minSize: 44,
    meta: { paddingLeft: "pl-3", disableTooltip: true },
    // Marks the row the out-of-date banner is about.
    cell: ({ row }) =>
      isRegenerable(row.original) && row.original.outOfDate ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span tabIndex={0} data-test="proposal-row-out-of-date">
              <IconContainer size="5" color="text-body-primary" as="span">
                <FileAlert />
              </IconContainer>
            </span>
          </TooltipTrigger>
          <TooltipContent enableMarkdown={false} maxWidth={20}>
            {t("contracts.proposals.outOfDate.rowTooltip")}
          </TooltipContent>
        </Tooltip>
      ) : (
        <IconContainer size="5" color="text-body-primary">
          <FileSigned />
        </IconContainer>
      )
  },
  {
    accessorKey: "name",
    header: t("contracts.proposals.columns.form"),
    enableSorting: false,
    size: nameWidth,
    meta: { disableTooltip: true },
    cell: ({ row }) => (
      <Text variant="body" className="truncate" hasTruncatedTooltip>
        {row.original.name}
      </Text>
    )
  },
  {
    accessorKey: "status",
    header: t("contracts.proposals.columns.status"),
    enableSorting: false,
    size: 150,
    meta: { disableTooltip: true },
    cell: ({ row }) => (
      <TrackedProposalStatus
        proposal={row.original}
        trackingPaused={trackingPaused}
      />
    )
  },
  {
    accessorKey: "createdAt",
    header: t("contracts.proposals.columns.createdAt"),
    enableSorting: false,
    size: 200,
    cell: ({ row }) => (
      <Text variant="body">
        {formatFullDate(toCalendarDate(row.original.createdAt))}
      </Text>
    )
  },
  {
    accessorKey: "updatedAt",
    header: t("contracts.proposals.columns.lastUpdate"),
    enableSorting: false,
    size: 200,
    cell: ({ row }) => (
      <Text variant="body">
        {formatFullDate(toCalendarDate(row.original.updatedAt))}
      </Text>
    )
  },
  {
    id: "createdBy",
    header: t("contracts.proposals.columns.createdBy"),
    enableSorting: false,
    size: 200,
    cell: ({ row }) => (
      <div className="flex items-center gap-2 min-w-0">
        <UserAvatar userData={row.original.createdBy} size="M" />
        <span className="truncate">{row.original.createdBy.displayName}</span>
      </div>
    )
  },
  {
    id: "actions",
    header: "",
    enableSorting: false,
    size: 64,
    minSize: 64,
    meta: { textAlign: "center", paddingLeft: "pl-2" },
    cell: ({ row }) => (
      <OptionsDropdownMenu
        options={rowOptions(
          row.original,
          actions,
          regenerateBlockedReason,
          regenerateHeld
        )}
        disabled={busyIds.has(row.original.id)}
        tPrefix="contracts.proposals.actions"
        triggerIcon="horizontal-ellipsis"
        iconSize="md"
        className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
        triggerDataTest="proposal-row-actions"
      />
    )
  }
];

export const ProposalsTable: React.FC<Props> = ({
  proposals,
  busyIds = NO_BUSY_IDS,
  regenerateBlockedReason,
  regenerateHeld = false,
  trackingPaused = false,
  onOpen,
  onOpenVersion,
  onRegenerate,
  onDelete,
  onRevise,
  onWithdraw
}) => {
  const { t } = useTranslation();
  const { ref, width } = useElementWidth<HTMLDivElement>();
  const nameWidth = formWidth(width);

  const columns = useMemo(
    () =>
      buildProposalColumns(
        t,
        { onOpen, onOpenVersion, onRegenerate, onDelete, onRevise, onWithdraw },
        busyIds,
        regenerateBlockedReason,
        regenerateHeld,
        nameWidth,
        trackingPaused
      ),
    [
      t,
      onOpen,
      onOpenVersion,
      onRegenerate,
      onDelete,
      onRevise,
      onWithdraw,
      busyIds,
      regenerateBlockedReason,
      regenerateHeld,
      nameWidth,
      trackingPaused
    ]
  );

  // Scrolls sideways only once the form can't shrink further, so the row menu
  // is never cut off.
  return (
    <div ref={ref}>
      <DataTable
        columns={columns}
        data={proposals}
        height="auto"
        className="overflow-x-auto"
        headerClassName="border-b border-border-border1 border-t-0"
        tableOptions={TABLE_OPTIONS}
        getRowClassName={() => "h-14"}
        // The same as the row menu's Edit or Open: a draft opens in the
        // editor, any other proposal or earlier version to be read. Held
        // while its proposal's action runs.
        onRowClick={(row) => {
          if (!busyIds.has(row.original.id)) {
            openRow(row.original, { onOpen, onOpenVersion });
          }
        }}
        dataTest="proposals-table"
      />
    </div>
  );
};
